import React, { useRef, useEffect, useState } from 'react';

const DrawingCanvas = ({ onDrawingChange, initialData }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const [tool, setTool] = useState('pen'); // pen, eraser, clear
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Configuration du canvas
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    
    setContext(ctx);

    // Charger les données initiales si disponibles
    if (initialData) {
      console.log('🎨 DrawingCanvas - Chargement initialData:', initialData ? `${initialData.substring(0, 50)}...` : 'VIDE');
      try {
        const img = new Image();
        img.onload = () => {
          console.log('✅ DrawingCanvas - Image chargée avec succès');
          ctx.drawImage(img, 0, 0);
        };
        img.onerror = () => {
          console.error('❌ DrawingCanvas - Erreur chargement image');
        };
        img.src = initialData;
      } catch (error) {
        console.error('❌ DrawingCanvas - Erreur lors du chargement des données de dessin:', error);
      }
    } else {
      console.log('🎨 DrawingCanvas - Aucune initialData fournie');
    }
  }, [initialData]);

  useEffect(() => {
    if (context) {
      context.strokeStyle = color;
      context.lineWidth = lineWidth;
      context.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
    }
  }, [color, lineWidth, tool, context]);

  const startDrawing = (e) => {
    if (!context) return;
    
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    
    // Sauvegarder le dessin
    if (onDrawingChange && canvasRef.current) {
      const dataURL = canvasRef.current.toDataURL();
      onDrawingChange(dataURL);
    }
  };

  const clearCanvas = () => {
    if (!context || !canvasRef.current) return;
    
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    if (onDrawingChange) {
      onDrawingChange('');
    }
  };

  const handleMouseDown = (e) => startDrawing(e);
  const handleMouseMove = (e) => draw(e);
  const handleMouseUp = () => stopDrawing();
  const handleMouseLeave = () => stopDrawing();

  // Support tactile
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    const mouseEvent = new MouseEvent('mouseup', {});
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Barre d'outils */}
      <div style={{
        position: 'absolute',
        top: '5px',
        left: '5px',
        zIndex: 10,
        background: 'rgba(255,255,255,0.9)',
        padding: '5px',
        borderRadius: '3px',
        display: 'flex',
        gap: '5px',
        alignItems: 'center',
        fontSize: '10px'
      }}>
        <button
          style={{
            padding: '2px 6px',
            border: '1px solid #ccc',
            background: tool === 'pen' ? '#9cb85c' : 'white',
            cursor: 'pointer',
            fontSize: '9px'
          }}
          onClick={() => setTool('pen')}
        >
          ✏️
        </button>
        
        <button
          style={{
            padding: '2px 6px',
            border: '1px solid #ccc',
            background: tool === 'eraser' ? '#9cb85c' : 'white',
            cursor: 'pointer',
            fontSize: '9px'
          }}
          onClick={() => setTool('eraser')}
        >
          🗑️
        </button>
        
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          style={{ width: '20px', height: '20px', border: 'none', cursor: 'pointer' }}
          disabled={tool === 'eraser'}
        />
        
        <input
          type="range"
          min="1"
          max="10"
          value={lineWidth}
          onChange={(e) => setLineWidth(parseInt(e.target.value))}
          style={{ width: '40px' }}
        />
        
        <button
          style={{
            padding: '2px 6px',
            border: '1px solid #ccc',
            background: 'white',
            cursor: 'pointer',
            fontSize: '9px'
          }}
          onClick={clearCanvas}
        >
          Effacer
        </button>
      </div>

      {/* Canvas de dessin */}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          cursor: tool === 'pen' ? 'crosshair' : tool === 'eraser' ? 'grab' : 'default',
          border: '1px solid #ddd'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
};

export default DrawingCanvas;


